// ============================================================
// KILLER OPENING DEFENCE DATA
// Each defense teaches the user how to survive and punish a killer opening
// Bot plays the attacker — user learns the correct defensive response
// ============================================================

const defenseOpenings = [
  // ==================== #1 DEFEND: SCHOLAR'S MATE ====================
  {
    id: 'defend-scholars-mate',
    name: "Defend: Scholar's Mate",
    originalOpening: 'scholars-mate',
    emoji: '🛡️',
    attackerColor: 'white',
    playerColor: 'black',
    difficulty: 'beginner',
    totalMoves: 5,
    description: "White tries Qh5 + Bc4 to mate on f7. Learn to shut it down and punish the early queen.",
    keyIdea: "Don't panic — develop pieces, block the threat, and punish the exposed queen.",
    steps: [
      {
        moveNumber: 1,
        attackerMove: 'e2e4',
        attackerExplanation: "White opens e4. Standard — nothing to worry about yet.",
        defensiveMove: 'e7e5',
        defenseExplanation: "Match in the center with e5. Solid and fights for space.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 2,
        attackerMove: 'f1c4',
        attackerExplanation: "⚠️ Bishop to c4 — it's aiming at f7! This is the start of the Scholar's Mate setup.",
        defensiveMove: 'b8c6',
        defenseExplanation: "Develop your knight! Nc6 defends e5 and controls the center. Don't worry about f7 yet.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 3,
        attackerMove: 'd1h5',
        attackerExplanation: "🚨 Queen to h5! Threatening Qxf7# — Scholar's Mate! The queen and bishop both target f7.",
        defensiveMove: 'g7g6',
        defenseExplanation: "🛡️ g6! Blocks the queen's attack on f7. The queen must retreat. You've wasted their time — their queen moved too early!",
        trapMove: 'g8f6',
        trapWarning: "❌ Nf6? That attacks the queen but DOESN'T defend f7! White plays Qxf7# — CHECKMATE! You need to block the diagonal first.",
        alternateDefense: 'd8e7',
        alternateExplanation: "Qe7 also works — it defends f7 directly. But g6 is better because it develops your game plan.",
      },
      {
        moveNumber: 4,
        attackerMove: 'h5f3',
        attackerExplanation: "White retreats to f3, still eyeing f7. But they've wasted two queen moves already!",
        defensiveMove: 'g8f6',
        defenseExplanation: "NOW develop Nf6! Attacks e4 and develops with tempo. You're ahead in development because they wasted time with the queen.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 5,
        attackerMove: 'f3b3',
        attackerExplanation: "Queen goes to b3, still trying to pressure f7 and b7.",
        defensiveMove: 'f6d5',
        defenseExplanation: "🏆 Nd5! Centralized knight, blocks the b3-f7 diagonal, and you're fully developed while White's queen has moved 3 times! You've completely punished the Scholar's Mate attempt.",
        trapMove: null,
        trapWarning: null,
        isDefenseComplete: true,
      }
    ],
  },

  // ==================== #2 DEFEND: FOOL'S MATE PATTERN ====================
  {
    id: 'defend-fools-mate',
    name: "Defend: Early Queen Attack",
    originalOpening: 'fools-mate',
    emoji: '🛡️',
    attackerColor: 'black',
    playerColor: 'white',
    difficulty: 'beginner',
    totalMoves: 4,
    description: "Black tries an early Qh4 to attack f2. Learn to defend and punish the premature queen.",
    keyIdea: "Don't weaken f2/g2. Develop pieces and the early queen becomes a target.",
    steps: [
      {
        moveNumber: 1,
        attackerMove: 'e7e5',
        attackerExplanation: "Black opens e5. Standard response.",
        defensiveMove: 'e2e4',
        defenseExplanation: "Open with e4! Control the center. This is always a safe, strong start.",
        trapMove: 'f2f3',
        trapWarning: "❌ f3? Terrible! This weakens your king's diagonal. If Black plays e5 then you play g4?? it's Qh4# — Fool's Mate! Never play f3 early.",
      },
      {
        moveNumber: 2,
        attackerMove: 'd8h4',
        attackerExplanation: "🚨 Black brings out the queen early to h4! Threatening nothing immediately, but eyeing f2 and e4.",
        defensiveMove: 'b1c3',
        defenseExplanation: "🛡️ Nc3! Develops a piece and defends e4. The queen on h4 is actually a liability for Black — it'll be chased around.",
        trapMove: 'g2g3',
        trapWarning: "g3 works but weakens the light squares. Nc3 is better — develop pieces instead of pushing pawns.",
        alternateDefense: 'g1f3',
        alternateExplanation: "Nf3 also works — develops and blocks the queen's attack on f2.",
      },
      {
        moveNumber: 3,
        attackerMove: 'f8c5',
        attackerExplanation: "Black develops the bishop, aiming at f2. They're trying to build threats.",
        defensiveMove: 'g1f3',
        defenseExplanation: "Nf3! Attacks the queen and develops another piece. The queen has to run!",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 4,
        attackerMove: 'h4f6',
        attackerExplanation: "Queen retreats to f6. Black has wasted two queen moves while you developed two pieces.",
        defensiveMove: 'd2d4',
        defenseExplanation: "🏆 d4! Seize the full center. You have two developed pieces, strong center control, and Black's queen is awkwardly placed. You've completely punished the early queen attack!",
        trapMove: null,
        trapWarning: null,
        isDefenseComplete: true,
      }
    ],
  },

  // ==================== #3 DEFEND: FRIED LIVER ATTACK ====================
  {
    id: 'defend-fried-liver',
    name: "Defend: Fried Liver Attack",
    originalOpening: 'fried-liver',
    emoji: '🛡️',
    attackerColor: 'white',
    playerColor: 'black',
    difficulty: 'intermediate',
    totalMoves: 6,
    description: "White sacrifices a knight on f7 to drag your king out. Learn to sidestep the trap.",
    keyIdea: "After Ng5, play d5! Then after exd5, play Na5 — NOT Nxd5.",
    steps: [
      {
        moveNumber: 1,
        attackerMove: 'e2e4',
        attackerExplanation: "e4 — standard opening.",
        defensiveMove: 'e7e5',
        defenseExplanation: "e5 — match the center. This is the Italian Game setup.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 2,
        attackerMove: 'g1f3',
        attackerExplanation: "Knight develops, attacking e5.",
        defensiveMove: 'b8c6',
        defenseExplanation: "Nc6 — defend e5. The Two Knights Defense is coming.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 3,
        attackerMove: 'f1c4',
        attackerExplanation: "Bishop to c4 — the Italian Game. Eyeing f7.",
        defensiveMove: 'g8f6',
        defenseExplanation: "Nf6 — the Two Knights Defense. Attacks e4. This invites the Fried Liver, but we know how to handle it!",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 4,
        attackerMove: 'f3g5',
        attackerExplanation: "💀 Ng5! White's knight and bishop BOTH target f7. This is the Fried Liver setup!",
        defensiveMove: 'd7d5',
        defenseExplanation: "🛡️ d5! The only good response. Counter in the center before White gets to sacrifice on f7.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 5,
        attackerMove: 'e4d5',
        attackerExplanation: "White takes the pawn. NOW the critical decision — don't take back with the knight!",
        defensiveMove: 'c6a5',
        defenseExplanation: "🛡️ Na5! The Traxler sidestep! Attacks the bishop on c4 and avoids the knight sacrifice on f7. White's attack is defused!",
        trapMove: 'f6d5',
        trapWarning: "❌ Nxd5? This is the TRAP! After Nxf7 Kxf7 Qf3+ your king gets dragged to the center and hunted down. This is the Fried Liver Attack — devastating!",
      },
      {
        moveNumber: 6,
        attackerMove: 'c4b5',
        attackerExplanation: "White tries Bb5+ but the attack is fizzling.",
        defensiveMove: 'c7c6',
        defenseExplanation: "🏆 c6! Block the check AND attack the bishop. After dxc6 bxc6, you'll win the bishop back. You've survived the Fried Liver AND have a great position!",
        trapMove: null,
        trapWarning: null,
        isDefenseComplete: true,
      }
    ],
  },

  // ==================== #4 DEFEND: BLACKBURNE SHILLING GAMBIT ====================
  {
    id: 'defend-blackburne-shilling',
    name: "Defend: Blackburne Shilling",
    originalOpening: 'blackburne-shilling',
    emoji: '🛡️',
    attackerColor: 'black',
    playerColor: 'white',
    difficulty: 'intermediate',
    totalMoves: 5,
    description: "Black plays Nd4 offering the e5 pawn. Don't take it — it's a smothered mate trap!",
    keyIdea: "When Black plays Nd4, take the knight with Nxd4 — NOT the e5 pawn with Nxe5.",
    steps: [
      {
        moveNumber: 1,
        attackerMove: 'e7e5',
        attackerExplanation: "Black plays e5. Normal.",
        defensiveMove: 'e2e4',
        defenseExplanation: "e4 — standard opening.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 2,
        attackerMove: 'b8c6',
        attackerExplanation: "Black develops the knight. Looks normal...",
        defensiveMove: 'g1f3',
        defenseExplanation: "Nf3 — develop and attack e5.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 3,
        attackerMove: 'c6d4',
        attackerExplanation: "⚠️ Nd4! Black's knight jumps forward, offering the e5 pawn. This is the Blackburne Shilling Gambit! Looks free but it's a TRAP.",
        defensiveMove: 'f1c4',
        defenseExplanation: "Bc4! Develop calmly. Don't grab e5. The knight on d4 isn't actually threatening much.",
        trapMove: 'f3e5',
        trapWarning: "❌ Nxe5? TRAP! Black plays Qg5, attacking your knight AND g2. After Nxf7 Qxg2 Rf1 Qxe4+ Be2 Nf3# — SMOTHERED MATE! Never take the bait!",
        alternateDefense: 'f3d4',
        alternateExplanation: "Nxd4 is also solid — just trade the knight and play exd4. Simple and safe.",
      },
      {
        moveNumber: 4,
        attackerMove: 'd8f6',
        attackerExplanation: "Black develops the queen, trying to create threats.",
        defensiveMove: 'd2d3',
        defenseExplanation: "d3 — solid development. Protect e4, open the bishop. You're developing normally while Black's knight on d4 will have to retreat.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 5,
        attackerMove: 'f8c5',
        attackerExplanation: "Black develops but the position is equal at best for them.",
        defensiveMove: 'c2c3',
        defenseExplanation: "🏆 c3! Kick the knight from d4. After Nb5 (or Ne6) you have the full center and great development. The gambit has failed!",
        trapMove: null,
        trapWarning: null,
        isDefenseComplete: true,
      }
    ],
  },

  // ==================== #5 DEFEND: LÉGAL'S MATE ====================
  {
    id: 'defend-legals-mate',
    name: "Defend: Légal's Mate",
    originalOpening: 'legals-mate',
    emoji: '🛡️',
    attackerColor: 'white',
    playerColor: 'black',
    difficulty: 'intermediate',
    totalMoves: 6,
    description: "White sacrifices the queen to deliver mate with minor pieces. Don't take the bait!",
    keyIdea: "When White plays Nxe5 offering the queen — DON'T take it! Retreat the bishop instead.",
    steps: [
      {
        moveNumber: 1,
        attackerMove: 'e2e4',
        attackerExplanation: "e4 — standard.",
        defensiveMove: 'e7e5',
        defenseExplanation: "e5 — match the center.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 2,
        attackerMove: 'g1f3',
        attackerExplanation: "Knight develops, attacks e5.",
        defensiveMove: 'd7d6',
        defenseExplanation: "d6 — the Philidor Defense. Solid pawn structure defending e5.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 3,
        attackerMove: 'f1c4',
        attackerExplanation: "Bishop to c4. Aiming at f7 again.",
        defensiveMove: 'c8g4',
        defenseExplanation: "Bg4 — pin the knight to the queen. This is a natural developing move.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 4,
        attackerMove: 'b1c3',
        attackerExplanation: "White develops the second knight. Building up...",
        defensiveMove: 'g7g6',
        defenseExplanation: "g6 — prepare to fianchetto the bishop. Solid development.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 5,
        attackerMove: 'f3e5',
        attackerExplanation: "💀 Nxe5! White sacrifices the pawn AND seems to leave the queen hanging to your bishop! This is Légal's trap — if you take the queen, it's CHECKMATE.",
        defensiveMove: 'g4e6',
        defenseExplanation: "🛡️ Be6! Retreat the bishop to safety. DON'T take the queen! You keep material and White's knight on e5 can be challenged.",
        trapMove: 'g4d1',
        trapWarning: "❌ Bxd1? TRAP! White plays Bxf7+ Ke7, then Nd5# — CHECKMATE! The queen sacrifice was bait! Always retreat the bishop instead.",
      },
      {
        moveNumber: 6,
        attackerMove: 'e5g4',
        attackerExplanation: "White's attack fizzles. The knight retreats awkwardly.",
        defensiveMove: 'b8c6',
        defenseExplanation: "🏆 Nc6! Develop and take control of the center. White wasted time with the failed trap, and you're developing normally. You've won the opening!",
        trapMove: null,
        trapWarning: null,
        isDefenseComplete: true,
      }
    ],
  },

  // ==================== #6 DEFEND: ENGLUND GAMBIT TRAP ====================
  {
    id: 'defend-englund-gambit',
    name: "Defend: Englund Gambit",
    originalOpening: 'englund-gambit',
    emoji: '🛡️',
    attackerColor: 'black',
    playerColor: 'white',
    difficulty: 'beginner',
    totalMoves: 4,
    description: "Black sacrifices a pawn with e5 against your d4, setting up Qb4+ fork. Avoid Bf4!",
    keyIdea: "After d4 e5 dxe5 Nc6 Nf3 Qe7 — don't play Bf4! Develop with Nc3 instead.",
    steps: [
      {
        moveNumber: 1,
        attackerMove: 'e7e5',
        attackerExplanation: "⚠️ e5 against your d4? This is the Englund Gambit! Black is offering a pawn...",
        defensiveMove: 'd2d4',
        defenseExplanation: "d4 — your solid opening. You can safely take the pawn.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 2,
        attackerMove: 'b8c6',
        attackerExplanation: "Black develops the knight. Putting pressure on e5.",
        defensiveMove: 'd4e5',
        defenseExplanation: "Take the pawn! dxe5 is fine. Don't be afraid — just be careful what you do next.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 3,
        attackerMove: 'd8e7',
        attackerExplanation: "⚠️ Qe7! Black's queen targets e5. They're setting up the Qb4+ fork if you develop wrong...",
        defensiveMove: 'g1f3',
        defenseExplanation: "Nf3 — develop the knight, defend e5. Safe and natural.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 4,
        attackerMove: 'c6e5',
        attackerExplanation: "Black takes back the pawn with the knight. Now watch what you develop next!",
        defensiveMove: 'b1c3',
        defenseExplanation: "🏆 Nc3! Develop safely. Do NOT play Bf4 — that's the trap! You've got solid development, two knights out, and Black's gambit has failed. You can continue with Be2 and castle safely.",
        trapMove: 'c1f4',
        trapWarning: "❌ Bf4? TRAP! Black plays Qb4+ — forking your king and b2 pawn. After you block, Qxb2 wins your rook on a1! Disaster!",
        alternateDefense: 'f1e2',
        alternateExplanation: "Be2 is also safe — just develop pieces without dropping the b2 pawn.",
        isDefenseComplete: true,
      }
    ],
  },

  // ==================== #7 DEFEND: BUDAPEST GAMBIT TRAP ====================
  {
    id: 'defend-budapest-gambit',
    name: "Defend: Budapest Gambit",
    originalOpening: 'budapest-gambit',
    emoji: '🛡️',
    attackerColor: 'black',
    playerColor: 'white',
    difficulty: 'intermediate',
    totalMoves: 5,
    description: "Black sacrifices a pawn and plays Ng4 after dxe5. Don't play e3 — it's a bomb!",
    keyIdea: "After dxe5 Ng4, play Bf4 to hold e5 — NOT e3, which allows Nxe3! fxe3 Qh4+.",
    steps: [
      {
        moveNumber: 1,
        attackerMove: 'g8f6',
        attackerExplanation: "Black plays Nf6. Looks like a normal Indian Defense.",
        defensiveMove: 'd2d4',
        defenseExplanation: "d4 — control the center. Standard.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 2,
        attackerMove: 'e7e5',
        attackerExplanation: "⚠️ e5!? The Budapest Gambit! Black offers the e-pawn to get active piece play.",
        defensiveMove: 'c2c4',
        defenseExplanation: "c4 first (before taking). Standard Queen's Gambit setup. Now you can take.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 3,
        attackerMove: 'f6g4',
        attackerExplanation: "💀 Ng4! Black's knight eyes e5 and the dangerous e3 square. This is where the trap is set!",
        defensiveMove: 'd4e5',
        defenseExplanation: "Take the pawn with dxe5. Now the critical moment — how to defend e5.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 4,
        attackerMove: 'd7d6',
        attackerExplanation: "Black challenges e5, trying to open lines.",
        defensiveMove: 'c1f4',
        defenseExplanation: "🛡️ Bf4! Defend e5 with the bishop. This is the KEY move. Solid and safe.",
        trapMove: 'e2e3',
        trapWarning: "❌ e3? TRAP! Black plays Nxe3! fxe3 Qh4+! Your king is caught in the center with a destroyed pawn structure. Devastating!",
        alternateDefense: 'g1f3',
        alternateExplanation: "Nf3 is also fine — develops and supports e5.",
      },
      {
        moveNumber: 5,
        attackerMove: 'b8c6',
        attackerExplanation: "Black develops, challenging e5.",
        defensiveMove: 'g1f3',
        defenseExplanation: "🏆 Nf3! Solid development. The knight on g4 is offside and will have to retreat. You kept the extra pawn and defused the gambit!",
        trapMove: null,
        trapWarning: null,
        isDefenseComplete: true,
      }
    ],
  },

  // ==================== #8 DEFEND: STAFFORD GAMBIT ====================
  {
    id: 'defend-stafford-gambit',
    name: "Defend: Stafford Gambit",
    originalOpening: 'stafford-gambit',
    emoji: '🛡️',
    attackerColor: 'black',
    playerColor: 'white',
    difficulty: 'intermediate',
    totalMoves: 5,
    description: "After Nxe5, Black plays Nc6 instead of the normal moves. Multiple traps await!",
    keyIdea: "After Nxe5 Nc6, retreat the knight to f3 — don't take on c6! Then d3 to consolidate.",
    steps: [
      {
        moveNumber: 1,
        attackerMove: 'e7e5',
        attackerExplanation: "e5 — standard. The Petrov Defense setup.",
        defensiveMove: 'e2e4',
        defenseExplanation: "e4 — open the game.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 2,
        attackerMove: 'g8f6',
        attackerExplanation: "Nf6 — the Petrov Defense. Attacks e4 instead of defending e5.",
        defensiveMove: 'g1f3',
        defenseExplanation: "Nf3 — attack e5.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 3,
        attackerMove: 'b8c6',
        attackerExplanation: "🚨 Nc6!? Instead of the normal d6 or Nxe4, Black offers the whole knight! This is the STAFFORD GAMBIT. Dangerous traps ahead!",
        defensiveMove: 'f3e5',
        defenseExplanation: "Take with Nxe5. You're up a piece, but be VERY careful what comes next.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 4,
        attackerMove: 'c6e5',
        attackerExplanation: "Black recaptures. Now — don't get greedy! Be cautious.",
        defensiveMove: 'd2d3',
        defenseExplanation: "🛡️ d3! Solid and safe. Defend e4, develop calmly. Don't touch Black's pieces — just consolidate your extra pawn.",
        trapMove: 'f3c6',
        trapWarning: "Nxc6 was the previous move — now just play d3. Don't try to be clever with d4 or Nc3 yet. Keep it simple.",
      },
      {
        moveNumber: 5,
        attackerMove: 'f8c5',
        attackerExplanation: "Black develops aggressively, aiming at f2.",
        defensiveMove: 'f1e2',
        defenseExplanation: "🏆 Be2! Develop and prepare to castle. After O-O you're safe with an extra pawn. The Stafford tricks only work if you get greedy — by playing solidly, you defuse everything!",
        trapMove: null,
        trapWarning: null,
        isDefenseComplete: true,
      }
    ],
  },

  // ==================== #9 DEFEND: ITALIAN GAME TRAP ====================
  {
    id: 'defend-italian-trap',
    name: "Defend: Italian Game Trap",
    originalOpening: 'italian-trap',
    emoji: '🛡️',
    attackerColor: 'black',
    playerColor: 'white',
    difficulty: 'advanced',
    totalMoves: 6,
    description: "Black lures you into d4, then Bb4+ pins your knight so Nxe4 wins a pawn. Play smart!",
    keyIdea: "After c3 Nf6, don't rush d4! Play d3 instead — slow and solid beats the trap.",
    steps: [
      {
        moveNumber: 1,
        attackerMove: 'e7e5',
        attackerExplanation: "e5 — standard response.",
        defensiveMove: 'e2e4',
        defenseExplanation: "e4 — open the game.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 2,
        attackerMove: 'b8c6',
        attackerExplanation: "Nc6 — defends e5.",
        defensiveMove: 'g1f3',
        defenseExplanation: "Nf3 — attack e5, develop.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 3,
        attackerMove: 'f8c5',
        attackerExplanation: "Bc5 — the Giuoco Piano (Italian Game).",
        defensiveMove: 'f1c4',
        defenseExplanation: "Bc4 — mirror their bishop. Standard Italian Game.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 4,
        attackerMove: 'g8f6',
        attackerExplanation: "Nf6 — develops and attacks e4. Now the critical moment: what do you play?",
        defensiveMove: 'd2d3',
        defenseExplanation: "🛡️ d3! The Giuoco Pianissimo — slow but SAFE. Defends e4, keeps your structure intact. No tricks can touch you.",
        trapMove: 'd2d4',
        trapWarning: "❌ d4? After exd4 cxd4 Bb4+! Your knight is forced to c3, where it's PINNED. Then Nxe4 wins your e-pawn because the knight can't recapture! The Italian Game Trap in action.",
      },
      {
        moveNumber: 5,
        attackerMove: 'd7d6',
        attackerExplanation: "Black develops normally. No tricks available against d3.",
        defensiveMove: 'c2c3',
        defenseExplanation: "c3 — prepare d4 for LATER, when your pieces are ready to support it.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 6,
        attackerMove: 'a7a6',
        attackerExplanation: "Black plays a6, preparing b5. Normal Italian maneuvering.",
        defensiveMove: 'e1g1',
        defenseExplanation: "🏆 Castle! King is safe, rook is active. You can play d4 later when you're fully developed. The Giuoco Pianissimo is a solid, trap-free way to play the Italian!",
        trapMove: null,
        trapWarning: null,
        isDefenseComplete: true,
      }
    ],
  },

  // ==================== #10 DEFEND: CARO-KANN SMOTHERED MATE ====================
  {
    id: 'defend-caro-kann-smother',
    name: "Defend: Caro-Kann Smother",
    originalOpening: 'caro-kann-smother',
    emoji: '🛡️',
    attackerColor: 'white',
    playerColor: 'black',
    difficulty: 'advanced',
    totalMoves: 5,
    description: "White sets up Nd6# smothered mate in the Caro-Kann. The wrong knight move order is fatal!",
    keyIdea: "After Ne4, develop the BISHOP first with Bf5 — NOT the knight to d7. Nd7 blocks your king's escape.",
    steps: [
      {
        moveNumber: 1,
        attackerMove: 'e2e4',
        attackerExplanation: "e4 — standard.",
        defensiveMove: 'c7c6',
        defenseExplanation: "c6 — the Caro-Kann Defense! Solid and reliable.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 2,
        attackerMove: 'd2d4',
        attackerExplanation: "d4 — full center control.",
        defensiveMove: 'd7d5',
        defenseExplanation: "d5 — challenge the center. Standard Caro-Kann.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 3,
        attackerMove: 'b1c3',
        attackerExplanation: "Nc3 — develops and protects e4.",
        defensiveMove: 'd5e4',
        defenseExplanation: "dxe4 — take the pawn. This is the main line of the Caro-Kann.",
        trapMove: null,
        trapWarning: null,
      },
      {
        moveNumber: 4,
        attackerMove: 'c3e4',
        attackerExplanation: "💀 Knight recaptures on e4. Now it's centralized and powerful. This is the CRITICAL moment — how you develop next determines everything!",
        defensiveMove: 'c8f5',
        defenseExplanation: "🛡️ Bf5! THE KEY MOVE! Develop the bishop FIRST. This is the correct Caro-Kann move order. The bishop gets out before the knight blocks it.",
        trapMove: 'b8d7',
        trapWarning: "❌ Nd7? DANGER! This blocks your king's escape route. If White plays Qe2 and you follow with Ngf6?? then Nd6# is SMOTHERED CHECKMATE! Your king is trapped by your own pieces. Always play Bf5 first!",
      },
      {
        moveNumber: 5,
        attackerMove: 'e4g3',
        attackerExplanation: "White plays Ng3 to challenge the bishop. Standard Caro-Kann.",
        defensiveMove: 'f5g6',
        defenseExplanation: "🏆 Bg6! Bishop retreats to a safe square. Now develop with e6, Nf6, Bd6 and you have a classic, solid Caro-Kann position. The smothered mate trick is impossible because your bishop developed first!",
        trapMove: null,
        trapWarning: null,
        isDefenseComplete: true,
      }
    ],
  },
];

export default defenseOpenings;
